import React, { useState } from 'react';
import { Mail, MapPin, Linkedin, Send, CheckCircle, FileText, DollarSign } from 'lucide-react';
import { portfolioData } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://formspree.io/f/xaqdqzog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
          _subject: `Portfolio Contact: ${formData.subject}`
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        setError('Failed to send message. Please try again or email directly.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again or email directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Let's <span className="text-amber-400">Connect</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Have a project in mind? Want to collaborate? I'd love to hear from you.
            Let's create something extraordinary together.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Get In <span className="text-amber-400">Touch</span>
              </h2>
              <p className="text-gray-400 text-lg mb-12 leading-relaxed">
                I'm always open to discussing new projects, creative collaborations, 
                or opportunities to be part of your vision. Feel free to reach out through 
                any of the channels below.
              </p>

              {/* Contact Methods */}
              <div className="space-y-6 mb-12">
                <a
                  href={`mailto:${portfolioData.contact.email}`}
                  className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-amber-400/30 rounded-2xl transition-all duration-300 hover:bg-white/10 group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center text-amber-400 group-hover:bg-amber-400 group-hover:text-black transition-all duration-300">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Email</p>
                    <p className="text-white font-semibold">{portfolioData.contact.email}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center text-amber-400">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Location</p>
                    <p className="text-white font-semibold">{portfolioData.contact.location}</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mb-8">
                <h3 className="text-white font-semibold mb-4">Follow Me</h3>
                <div className="flex gap-4">
                  <a
                    href={portfolioData.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/5 hover:bg-amber-400/20 border border-white/10 hover:border-amber-400 flex items-center justify-center text-gray-400 hover:text-amber-400 transition-all duration-300"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={20} />
                  </a>
                </div>
              </div>

              {/* Download Resources */}
              <div>
                <h3 className="text-white font-semibold mb-4">Resources</h3>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="/downloads/scott-parrish-resume.pdf"
                    download="Scott_Parrish_Resume_2026.pdf"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-400 text-black font-semibold rounded-full hover:bg-amber-300 transition-all duration-300 text-sm"
                    data-testid="contact-download-resume-btn"
                  >
                    <FileText size={18} />
                    Resume
                  </a>
                  <a
                    href="/downloads/scott-parrish-rate-card.pdf"
                    download="Scott_Parrish_Rate_Card_2026.pdf"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 hover:border-amber-400/50 transition-all duration-300 text-sm"
                    data-testid="contact-download-rate-card-btn"
                  >
                    <DollarSign size={18} />
                    Rate Card
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="relative">
              <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 disabled:opacity-50"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 disabled:opacity-50"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-white mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 disabled:opacity-50"
                        placeholder="Project Inquiry"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-white mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        rows="6"
                        className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 resize-none disabled:opacity-50"
                        placeholder="Tell me about your project..."
                      />
                    </div>

                    {error && (
                      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-amber-400 text-black font-semibold rounded-xl hover:bg-amber-300 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                      {!isSubmitting && <Send size={20} className="group-hover:translate-x-1 transition-transform duration-300" />}
                    </button>
                  </form>
                ) : (
                  <div className="py-12 text-center">
                    <div className="w-16 h-16 bg-amber-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-amber-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-gray-400">
                      Thank you for reaching out. I'll get back to you soon.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Quote */}
      <section className="py-24 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-12 text-center">
            <blockquote className="text-2xl md:text-3xl text-white font-bold mb-4 italic">
              "{portfolioData.philosophy}"
            </blockquote>
            <p className="text-gray-400">
              Let's evolve visual knowledge together.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;